import React, { useState, useEffect } from 'react';
import { generateExamPinInCloud, deactivateExamPinInCloud, listenToUnifiedCollection } from '../config/firebase';

export default function TeacherExamPinManager() {
  const [selectedGrade, setSelectedGrade] = useState('6');
  const [selectedUnit, setSelectedUnit] = useState(1);
  const [activePin, setActivePin] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600); // en segundos
  const [examResults, setExamResults] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Cargar PIN activo si existe o generar uno de inmediato automáticamente
  useEffect(() => {
    const initPin = async () => {
      const savedPin = localStorage.getItem('motor_pedagogico_last_active_pin');
      if (savedPin) {
        try {
          const parsed = JSON.parse(savedPin);
          const expTime = new Date(parsed.expiresAt).getTime();
          const nowTime = new Date().getTime();
          if (nowTime < expTime && parsed.isActive !== false) {
            setActivePin(parsed);
            setSelectedGrade(String(parsed.grade));
            setSelectedUnit(Number(parsed.unit));
            setTimeLeft(Math.floor((expTime - nowTime) / 1000));
            return;
          }
        } catch (e) {}
      }

      // Si no hay PIN activo o expiró, generar uno automáticamente de inmediato
      try {
        const autoPin = await generateExamPinInCloud('6', 1, "Prof. Didier");
        setActivePin(autoPin);
        setTimeLeft(600);
      } catch (e) {}
    };

    initPin();
  }, []);

  // Escuchar cambios de PINs e ingresos en tiempo real desde Firestore
  useEffect(() => {
    const unsubPins = listenToUnifiedCollection("exam_pins", (cloudPins) => {
      if (!activePin) return;
      const matching = cloudPins.find(p => String(p.pin).trim() === String(activePin.pin).trim());
      if (matching) {
        setActivePin(prev => ({ ...prev, ...matching }));
      }
    });

    const unsubResults = listenToUnifiedCollection("activities", (activities) => {
      // Filtrar resultados de evaluaciones tipo Práctica ICFES / Examen
      const examActs = activities.filter(a => a.type && (a.type.includes('ICFES') || a.type.includes('Evaluación')));
      examActs.sort((a, b) => new Date(b.timestamp || b.date).getTime() - new Date(a.timestamp || a.date).getTime());
      setExamResults(examActs);
    });

    return () => {
      unsubPins();
      unsubResults();
    };
  }, [activePin]);

  // Temporizador de cuenta regresiva de 10 minutos
  useEffect(() => {
    if (!activePin || timeLeft <= 0 || activePin.isActive === false) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [activePin, timeLeft]);

  // Generar un nuevo PIN dinámico en la nube Firestore
  const handleGenerateNewPin = async () => {
    setIsGenerating(true);
    try {
      const newPinData = await generateExamPinInCloud(selectedGrade, selectedUnit, "Prof. Didier");
      setActivePin(newPinData);
      setTimeLeft(600); // 10 minutos = 600 segundos
      setCopied(false);
    } catch (e) {
      console.error("Error generando PIN:", e);
    } finally {
      setIsGenerating(false);
    }
  };

  // Desactivar manualmente el PIN activo
  const handleDeactivatePin = async () => {
    if (!activePin) return;
    await deactivateExamPinInCloud(activePin.pin);
    setActivePin(prev => ({ ...prev, isActive: false }));
  };

  const copyPinToClipboard = () => {
    if (!activePin) return;
    navigator.clipboard.writeText(activePin.pin);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Formatear segundos a MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const studentsEnteredList = Array.isArray(activePin?.studentsEntered) ? activePin.studentsEntered : [];

  return (
    <div className="space-y-6 text-slate-100 animate-fadeIn">
      
      {/* ENCABEZADO Y CONTROLES DE GENERACIÓN */}
      <div className="bg-slate-900/90 border border-slate-800 p-6 rounded-3xl space-y-4 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
          <div>
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-purple-300 bg-purple-950 px-2.5 py-0.5 rounded-full border border-purple-500/30">
              Evaluación Sincronizada ICFES/Saber (15 Preguntas)
            </span>
            <h3 className="text-xl font-extrabold text-white mt-1">
              🔑 Generador de PIN Dinámico en la Nube (Multidispositivo)
            </h3>
            <p className="text-xs text-slate-400">
              Genera un código temporal de 10 minutos sincronizado con Firebase Firestore. Los estudiantes pueden ingresar desde cualquier celular o computador simultáneamente.
            </p>
          </div>
        </div>

        {/* SELECTORES DE GRADO Y UNIDAD */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Seleccionar Grado:</label>
            <select 
              value={selectedGrade} 
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-white outline-none focus:border-purple-500 cursor-pointer"
            >
              <option value="6">Grado 6° - Fundamentos</option>
              <option value="7">Grado 7° - Task-Based Learning</option>
              <option value="9">Grado 9° - Competencias Avanzadas</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Seleccionar Unidad Académica:</label>
            <select 
              value={selectedUnit} 
              onChange={(e) => setSelectedUnit(Number(e.target.value))}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-white outline-none focus:border-purple-500 cursor-pointer"
            >
              <option value={1}>Unidad 1</option>
              <option value={2}>Unidad 2</option>
              <option value={3}>Unidad 3</option>
              <option value={4}>Unidad 4</option>
              <option value={5}>Unidad 5</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleGenerateNewPin}
          disabled={isGenerating}
          className="w-full bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold py-4 rounded-2xl shadow-xl transition cursor-pointer text-sm flex items-center justify-center gap-2 transform hover:scale-[1.01] disabled:opacity-50"
        >
          {isGenerating ? '⏳ Sincronizando PIN con la nube...' : '🚀 Generar Nuevo PIN de Acceso (Válido 10 Minutos)'}
        </button>
      </div>

      {/* MONITOR EN VIVO DEL PIN GENERADO */}
      {activePin && (
        <div className="bg-slate-900 border-2 border-purple-500/60 p-6 md:p-8 rounded-3xl shadow-2xl space-y-4 text-center relative overflow-hidden">
          
          <div className="text-[11px] font-black uppercase tracking-widest text-purple-300">
            PIN Activo en Firestore para Grado {activePin.grade}° — Unidad {activePin.unit}
          </div>

          {/* VISUALIZADOR DEL CÓDIGO DE 6 DÍGITOS */}
          <div className="bg-slate-950 border-2 border-purple-500/40 p-6 md:p-8 rounded-2xl inline-block mx-auto max-w-sm w-full shadow-2xl">
            <div className="text-5xl md:text-6xl font-black font-mono tracking-widest text-purple-400 select-all">
              {activePin.pin}
            </div>

            {/* ESTADO DEL PIN Y TEMPORIZADOR */}
            <div className="mt-4">
              {activePin.isActive === false ? (
                <span className="inline-block bg-red-950 text-red-300 text-xs font-black px-4 py-1.5 rounded-full border border-red-500/50 shadow-lg">
                  🚫 ACCESO CERRADO POR EL PROFESOR
                </span>
              ) : timeLeft > 0 ? (
                <div className="space-y-2">
                  <span className="inline-block bg-emerald-950 text-emerald-300 text-xs font-extrabold px-4 py-1.5 rounded-full border border-emerald-500/50 shadow-md">
                    🟢 ACTIVO EN LA NUBE (TODOS LOS DISPOSITIVOS)
                  </span>
                  <div className="text-xs text-slate-300 font-mono">
                    Tiempo de validez restante: <span className="text-amber-400 font-black text-base">{formatTime(timeLeft)}</span>
                  </div>
                </div>
              ) : (
                <span className="inline-block bg-amber-950 text-amber-300 text-xs font-bold px-4 py-1.5 rounded-full border border-amber-500/50">
                  ⏰ EXPIRADO (Transcurrieron 10 minutos)
                </span>
              )}
            </div>

            <div className="flex justify-center gap-2 mt-4 flex-wrap">
              <button
                onClick={copyPinToClipboard}
                className="text-xs font-bold text-purple-300 hover:text-white bg-purple-950/80 hover:bg-purple-900 border border-purple-500/40 px-4 py-1.5 rounded-xl transition cursor-pointer"
              >
                {copied ? '✓ ¡PIN Copiado!' : '📋 Copiar PIN'}
              </button>

              {activePin.isActive !== false && timeLeft > 0 && (
                <button
                  onClick={handleDeactivatePin}
                  className="text-xs font-bold text-red-300 hover:text-white bg-red-950/80 hover:bg-red-900 border border-red-500/40 px-4 py-1.5 rounded-xl transition cursor-pointer"
                >
                  🔒 Cerrar Acceso Ahora
                </button>
              )}
            </div>
          </div>

          <p className="text-xs text-slate-300 max-w-md mx-auto leading-relaxed">
            Proyecta este código en el salón. Cualquier estudiante puede ingresar desde su celular o computador tocando el botón <strong>📝 Acceso a Evaluación</strong> y digitando el PIN <strong>{activePin.pin}</strong>.
          </p>

          {/* LISTA DE ESTUDIANTES CONECTADOS CON ESTE PIN */}
          {studentsEnteredList.length > 0 && (
            <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-2xl max-w-md mx-auto text-left space-y-2">
              <div className="flex justify-between items-center text-xs font-bold text-emerald-400 border-b border-slate-800 pb-2">
                <span>👥 Estudiantes que han ingresado:</span>
                <span className="bg-emerald-950 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-500/30">
                  {studentsEnteredList.length} alumnos
                </span>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 pr-1 text-[11px]">
                {studentsEnteredList.map((st, sIdx) => (
                  <div key={sIdx} className="flex justify-between text-slate-300 bg-slate-900/60 px-2.5 py-1 rounded-lg">
                    <span className="font-semibold text-white">✓ {st.name || 'Estudiante'}</span>
                    <span className="text-slate-500 font-mono">{new Date(st.enteredAt || st.time).toLocaleTimeString('es-CO')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MONITOR DE RESULTADOS DE EVALUACIONES EN TIEMPO REAL */}
      <div className="bg-slate-900/90 border border-slate-800 p-6 rounded-3xl space-y-4 shadow-xl">
        <div className="flex justify-between items-center border-b border-slate-800 pb-3">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-200 flex items-center gap-2">
            <span>📈</span> Resultados de Evaluaciones en Tiempo Real ({examResults.length})
          </h4>
          <span className="text-[10px] text-emerald-400 font-bold">● Sincronizado en vivo con Firebase</span>
        </div>

        {examResults.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 uppercase text-[10px] bg-slate-950/60">
                  <th className="p-3">Estudiante</th>
                  <th className="p-3">Grupo</th>
                  <th className="p-3">Tema Evaluado</th>
                  <th className="p-3">Puntaje</th>
                  <th className="p-3">Porcentaje</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">Temas a Reforzar</th>
                  <th className="p-3">XP</th>
                  <th className="p-3">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-200">
                {examResults.map((res, index) => {
                  const isApproved = Number(res.pct) >= 80;
                  const statusLabel = res.status || (isApproved ? 'Aprobado' : 'No aprobado');

                  return (
                    <tr key={index} className="hover:bg-slate-800/40 transition">
                      <td className="p-3 font-bold text-white">{res.name}</td>
                      <td className="p-3 font-mono text-slate-300">{res.group}</td>
                      <td className="p-3 text-slate-300">{res.topic}</td>
                      <td className="p-3 font-mono font-bold text-purple-300">{res.score}</td>
                      <td className="p-3">
                        <span className={`font-bold ${isApproved ? 'text-emerald-400' : 'text-red-400'}`}>
                          {res.pct}%
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                          isApproved 
                            ? 'bg-emerald-950 text-emerald-300 border-emerald-500/50' 
                            : 'bg-red-950 text-red-300 border-red-500/50'
                        }`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="p-3 text-[11px]">
                        {res.topicsToImprove && res.topicsToImprove.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {res.topicsToImprove.map((t, idx) => (
                              <span key={idx} className="bg-amber-950/80 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded text-[10px]">
                                {t.category || t}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-emerald-400 font-semibold">Dominio Completo ✨</span>
                        )}
                      </td>
                      <td className="p-3 font-bold text-amber-400">+{res.xp} XP</td>
                      <td className="p-3 text-slate-400">{res.date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6 text-xs text-slate-400">
            No se han registrado entregas de evaluaciones en esta sesión.
          </div>
        )}
      </div>

    </div>
  );
}
